/**
 * "Op lengte"-artikelen (per strekkende meter geprijsd). Scope nu: U-profielen.
 *
 * Bij deze artikelen is de opgeslagen `priceCents` de prijs PER METER (zo levert de
 * Timmers-API 'm aan), niet de prijs van het hele handelslengte-stuk. De klant kiest
 * een lengte; prijs = prijs/meter × lengte. De handelslengte (max) staat in de naam
 * ("… 5000mm …"). Eén bron, gedeeld door client (productpagina/winkelwagen) en server.
 */

/** Subgroep-naam (Timmers) van de op-lengte-artikelen. */
export const LENGTH_ARTICLE_SUBGROEP = "U-profielen";
/** Subgroep van de Duco-beglazingsrubbers: maatwerk per meter (van de rol). */
export const RUBBER_ARTICLE_SUBGROEP = "Beglazingsrubbers";
/** Minimale bestelbare lengte (mm). */
export const LENGTH_ARTICLE_MIN_MM = 100;
/**
 * Minimum voor een profiel dat PER METER wordt ingekocht (geen staaf die we toch heel afnemen).
 *
 * Bij die artikelen is de meter de eenheid waarin Timmers levert én factureert: hun prijs staat
 * als "per m1"/"per mt" en het gewicht is per meter. Een ondergrens van 100 mm betekende dat we
 * € 2,92 rekenden voor een stuk waarvan de inkoop € 21,64 is — verlies op elke korte order. Bij
 * een profiel met een bekende handelslengte blijft de oude ondergrens staan: daar zagen we uit een
 * staaf die we hoe dan ook afnemen, en de admin bepaalt of zagen mag. (Thijs, 28-10-2026.)
 */
export const PER_METER_MIN_MM = 1000;
/** Maximale lengte in één stuk voor rubber-op-de-rol (mm). */
export const RUBBER_ARTICLE_MAX_MM = 6000;

export interface LengthArticleBounds {
  /** Handelslengte = maximale lengte in één stuk (mm). */
  maxLengthMm: number;
  /** Minimale bestelbare lengte (mm). */
  minLengthMm: number;
  /**
   * Is `maxLengthMm` een échte handelslengte van de leverancier, of onze eigen aanname? De
   * productpagina zette er onvoorwaardelijk "(handelslengte)" bij, ook achter de terugvalwaarde van
   * 5000 mm — dat leest als een harde leverancierslengte terwijl wij het verzonnen. (28-10-2026.)
   */
  maxIsHandelslengte: boolean;
}

/**
 * Hoe verkoopt Timmers dit profiel? De API kent daar geen veld voor; de sync leest het uit de
 * omschrijving ("Handelslengte = 6000mm", "per m1", "op maat gezaagde lengte") en zet het op het
 * artikel. Zo gebruiken de webshop en de server dezelfde regel. (Thijs, 29-07-2026.)
 */
export interface ProfielVerkoop {
  /** Prijs is per strekkende meter (de klant kiest een lengte). */
  perM1: boolean;
  /** Lengte van één handelslengte in mm; null = niet bekend uit de gegevens. */
  handelslengteMm: number | null;
  /** Timmers kan dit profiel op maat zagen (de klant geeft de maat door). */
  opMaatMogelijk: boolean;
}

/**
 * Gaat dit artikel over lengte, zodat de verkoopwijze hieronder iets betekent?
 *
 * Dit was de subgroep "U-profielen" en niets anders. Dat hield de regel veilig zolang alleen die
 * subgroep lengtevelden kreeg, maar het maakte de rest van de catalogus onbereikbaar: de sync zette
 * `perM1` netjes op de PEXT-overkappingsprofielen en core gooide het hier weg, dus bleven ze per
 * stuk te koop terwijl Timmers ze per meter levert. Nu is het gegeven de poort: heeft een artikel
 * een verkoopwijze (uit de omschrijving of met de hand in Productbeheer gezet), dan geldt die ook.
 * De subgroep blijft meetellen, zodat het vinkje bij een U-profiel zonder API-tekst blijft werken.
 * (Thijs, 28-10-2026.)
 */
function isProfielArtikel(art: ProfielArtikel, ov?: ProfielOverrides | null): boolean {
  if (art.subgroep === LENGTH_ARTICLE_SUBGROEP) return true;
  if (art.slug && ov?.profielVerkoopPerSlug?.[art.slug]) return true;
  return art.perM1 === true || art.opMaatMogelijk === true || art.alleenHandelslengte === true;
}

export function profielVerkoop(art: ProfielArtikel, ov?: ProfielOverrides | null): ProfielVerkoop | null {
  if (!isProfielArtikel(art, ov)) return null;
  const hand = ov?.profielVerkoopPerSlug?.[art.slug ?? ""];
  return {
    // `perM1` zegt alléén iets over de PRIJSEENHEID: is de opgeslagen prijs een meterprijs?
    // Dat staat los van de vraag of er gezaagd mag worden, en die twee zijn eerder door elkaar
    // gelopen: toen "afname per handelslengte" ook `perM1` uitzette, verscheen de METERPRIJS als
    // stuksprijs op de pagina — vijf keer te weinig voor een staaf van 5 m.
    perM1: hand?.perM1 ?? art.perM1 === true,
    handelslengteMm: hand?.handelslengteMm ?? art.handelslengteMm ?? null,
    // Zegt de leverancier zélf "Afname per handelslengte", dan wint die tekst van het vinkje in
    // Productbeheer. Bij order 1050 stond dat vinkje aan omdat de webshop van Timmers een gezaagde
    // lengte aanbood; dat bleek een fout aan hún kant en wij moesten alsnog een hele staaf van 5 m
    // afnemen. Een vinkje mag niet beloven wat de leverancier niet levert. (03-10-2026.)
    opMaatMogelijk: art.alleenHandelslengte === true ? false : (hand?.opMaatMogelijk ?? art.opMaatMogelijk === true),
  };
}

/** Alleen het stukje overrides dat over profielen gaat — zo hoeft core niets van de rest te weten. */
export interface ProfielOverrides {
  profielVerkoopPerSlug?: Record<string, { perM1?: boolean; handelslengteMm?: number; opMaatMogelijk?: boolean }>;
}

interface ProfielArtikel {
  slug?: string;
  subgroep?: string;
  name?: string;
  perM1?: boolean;
  handelslengteMm?: number;
  opMaatMogelijk?: boolean;
  /**
   * De omschrijving van Timmers zegt "Afname per handelslengte van X mm" — dan is een gezaagde
   * lengte of een meterprijs niet te bestellen, wat een vinkje in Productbeheer ook zegt.
   * De sync zet dit veld; let op het verschil met een losse vermelding "Handelslengte = 6000mm",
   * die alleen de lengte van de staaf noemt en zagen niet uitsluit.
   */
  alleenHandelslengte?: boolean;
  /**
   * De langste lengte die de leverancier kan leveren ("Max. lengte: 7150mm"), als die bekend is.
   * Niet hetzelfde als een handelslengte: je hoeft zo'n staaf niet héél af te nemen, hij is alleen
   * niet langer te krijgen. Zonder dit veld viel de bovengrens terug op 5000 mm en kon een klant
   * geen profiel van zes meter bestellen terwijl Timmers tot 7150 mm gaat. (28-10-2026.)
   */
  maxLengteMm?: number;
}

/**
 * Is dit een per-meter-artikel? Zo ja: de lengte-grenzen; anders null.
 *
 * Beglazingsrubber gaat van de rol en is dus altijd per meter. Een U-profiel alleen als de
 * omschrijving "per m1" zegt — de rest verkoopt Timmers per hele handelslengte, en dan is de
 * prijs een STUKprijs. Die stond hier eerder als meterprijs, waardoor een profiel van € 28,55
 * per handelslengte als 5 × € 28,55 in de winkelwagen kwam.
 */
export function lengthArticleBounds(art: ProfielArtikel, ov?: ProfielOverrides | null): LengthArticleBounds | null {
  if (art.subgroep === RUBBER_ARTICLE_SUBGROEP)
    return { maxLengthMm: RUBBER_ARTICLE_MAX_MM, minLengthMm: LENGTH_ARTICLE_MIN_MM, maxIsHandelslengte: false };
  const v = profielVerkoop(art, ov);
  if (!v || !v.perM1) return null;
  const m = (art.name ?? "").match(/(\d{3,4})\s*mm/);
  // Volgorde: een handelslengte bindt (zo lang is de staaf), daarna de door de leverancier genoemde
  // maximale lengte, dan een maat uit de naam, en pas als niets bekend is de aanname van 5 m.
  const max = v.handelslengteMm ?? art.maxLengteMm ?? (m ? Number(m[1]) : 5000);
  // Geen handelslengte bekend = we kopen per meter in, dus minimaal één meter afnemen.
  const min = v.handelslengteMm == null ? PER_METER_MIN_MM : LENGTH_ARTICLE_MIN_MM;
  return { maxLengthMm: max, minLengthMm: min, maxIsHandelslengte: v.handelslengteMm != null };
}

/**
 * Een profiel dat alléén per hele handelslengte te koop is: wat kost één staaf, en hoe lang is die?
 *
 * Nodig omdat de opgeslagen prijs een METERprijs is. Op een tegel of in een lijstje "€ 20,80 /m"
 * tonen terwijl je minimaal een staaf van 5 m moet afnemen, wekt precies de verwachting die bij
 * order 1050 misging. Geeft null als het artikel gewoon per meter of per stuk verkocht wordt —
 * dan verandert er niets aan de weergave.
 */
export function heleStaafPrijsCents(
  perMeterCents: number,
  art: ProfielArtikel,
  ov?: ProfielOverrides | null,
): { prijsCents: number; lengteMm: number } | null {
  if (art.alleenHandelslengte !== true) return null;
  const v = profielVerkoop(art, ov);
  if (!v?.perM1 || !v.handelslengteMm) return null;
  return { prijsCents: lengthArticlePriceCents(perMeterCents, v.handelslengteMm), lengteMm: v.handelslengteMm };
}

/**
 * Mag dit op-lengte-artikel op maat gezaagd worden? Drie bronnen, en ze tellen allemaal mee:
 * beglazingsrubber (altijd), de losse admin-schakelaar `cutToLength`, en de verkoopwijze van het
 * profiel (`opMaatMogelijk`, uit de Timmers-omschrijving of handmatig gezet). Die laatste telde
 * eerder niet mee, waardoor "op maat zaagbaar" aanzetten geen lengteveld opleverde.
 */
export function magOpMaat(art: ProfielArtikel, ov?: (ProfielOverrides & { cutToLength?: string[] }) | null): boolean {
  if (isAlwaysCutToLength(art)) return true;
  if (art.slug && ov?.cutToLength?.includes(art.slug)) return true;
  return profielVerkoop(art, ov)?.opMaatMogelijk === true;
}

/** Beglazingsrubbers zijn inherent maatwerk (per meter van de rol) → altijd op maat, geen admin-gate. */
export function isAlwaysCutToLength(art: { subgroep?: string }): boolean {
  return art.subgroep === RUBBER_ARTICLE_SUBGROEP;
}

/** Is dit een beglazingsrubber-artikel (opgeslagen prijs = Duco-inkoop, marge komt er runtime bij)? */
export function isRubberArticle(art: { subgroep?: string }): boolean {
  return art.subgroep === RUBBER_ARTICLE_SUBGROEP;
}

/**
 * Een sub-variant erft de verkoopwijze van zijn BASISARTIKEL.
 *
 * Waarom dit moet. Een profiel heeft kleurvarianten met elk een eigen slug, maar de klant kiest
 * zijn lengte op de pagina van het BASISartikel — daar staat de instelling "per m1" ook. Legt hij
 * een variant in de winkelwagen, dan draagt de regel de variant-slug. Kijkt de server dan alleen
 * naar die slug, dan vindt hij geen `perM1`, ziet hij geen op-lengte-artikel, en rekent hij de
 * METERPRIJS als stuksprijs af.
 *
 * Dat is bij order 1035 gebeurd: 2,5 m U-profiel (regel 8) ging voor de prijs van 1 meter de deur
 * uit, terwijl de bon "Op maat gezaagd: 2500 mm" vermeldde en Timmers 2,5 m in rekening brengt.
 * De klant zag in de winkelwagen het juiste bedrag; de server rekende het af als één meter.
 *
 * Regel: een eigen instelling op de variant wint altijd; staat die er niet, dan geldt die van het
 * basisartikel. Precies wat de productpagina al deed.
 */
export function metProfielVanBasis<T extends ProfielArtikel>(
  art: T,
  basis: ProfielArtikel | null | undefined,
  ov?: ProfielOverrides | null,
): T {
  if (!basis || !basis.slug || basis.slug === art.slug) return art;
  const eigen = ov?.profielVerkoopPerSlug?.[art.slug ?? ""];
  // Heeft de variant zélf een instelling, dan is dat een bewuste keuze — niet overschrijven.
  if (eigen && (eigen.perM1 != null || eigen.opMaatMogelijk != null || eigen.handelslengteMm != null)) return art;
  const vanBasis = ov?.profielVerkoopPerSlug?.[basis.slug] ?? {};
  return {
    ...art,
    subgroep: art.subgroep ?? basis.subgroep,
    perM1: art.perM1 ?? vanBasis.perM1 ?? basis.perM1,
    handelslengteMm: art.handelslengteMm ?? vanBasis.handelslengteMm ?? basis.handelslengteMm,
    opMaatMogelijk: art.opMaatMogelijk ?? vanBasis.opMaatMogelijk ?? basis.opMaatMogelijk,
  };
}

/** Prijs (cents, excl. btw) voor een gezaagde lengte tegen de meterprijs. */
export function lengthArticlePriceCents(perMeterCents: number, lengthMm: number): number {
  return Math.round((perMeterCents * lengthMm) / 1000);
}
